import { inject } from '@angular/core';
import { UrlTree } from '@angular/router';
import { AuthService } from 'app/core/services/auth.service';
import { Observable, of, switchMap } from 'rxjs';
import { IUser } from '../models/auth.model';

/**
 * Shared authenticated-session preamble for guards that deny guests by handing
 * off to IAM (RoleGuard, GlobalOrgAdminGuard, BillingAccessGuard).
 *
 * Runs `AuthService.check()`; an unauthenticated result (or the authenticated
 * edge case with a null user) calls `loginWithIam(returnUrl)` and denies the
 * navigation with `false` — the hand-off navigates externally, so there is no
 * UrlTree to return. An authenticated result is passed to `decide`, which owns
 * each guard's own allow/deny logic (role checks, `/forbidden` UrlTree, etc.).
 *
 * Guards with a different unauthenticated contract — silent sign-out →
 * `/sign-in` (AuthGuard) or `/sign-in?returnUrl=` (RoleRedirectGuard,
 * NoAuthGuard) — do not use this helper.
 *
 * @param returnUrl URL IAM should return the user to after login.
 * @param decide Called with the authenticated check result (`user` non-null).
 */
export function requireAuthSession(
    returnUrl: string,
    decide: (auth: {
        isAuthenticated: boolean;
        user: IUser;
    }) => Observable<boolean | UrlTree>
): Observable<boolean | UrlTree> {
    const authService = inject(AuthService);
    return authService.check().pipe(
        switchMap((auth) => {
            if (!auth.isAuthenticated || !auth.user) {
                authService.loginWithIam(returnUrl);
                return of(false);
            }

            return decide({
                isAuthenticated: auth.isAuthenticated,
                user: auth.user,
            });
        })
    );
}
